<template>
  <Head title="My Profile" />

  <div class="max-w-4xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">My Profile</h1>
          <InfoButton title="Managing Your Profile" variant="default">
            <p>
              <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
              The Profile page is your personal account management hub where you can view and manage all
              aspects of your user account. Here you can review your personal information, manage security
              settings including password and two-factor authentication (2FA), monitor active sessions,
              manage trusted devices for 2FA bypass, and access your account status information. Keeping
              your profile information current and security settings properly configured ensures optimal
              system functionality, maintains account security, enables proper communication from the system,
              and protects your account from unauthorized access.
            </p>
            <p>
              <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
              • <strong>Edit Profile Button:</strong> Click to update your personal information including
                name, email, phone number, and theme preference<br>
              • <strong>Profile Header:</strong> View your avatar, full name, email address, verification
                status, and account status at a glance<br>
              • <strong>Personal Information:</strong> Review your contact details, theme preference, account
                creation date, and last login timestamp<br>
              • <strong>Security Settings:</strong> Access password change functionality, two-factor
                authentication management, active session monitoring, and trusted device management<br>
              • <strong>Email Verification Badge:</strong> Green "Email Verified" badge means your email is
                confirmed, yellow "Email Not Verified" badge indicates you need to verify your email<br>
              • <strong>Active/Inactive Badge:</strong> Shows current account status - active accounts can
                access the system, inactive accounts cannot login
            </p>
            <p>
              <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
              • <strong>Verify your email address</strong> - Email verification is required for password
                recovery and system notifications<br>
              • <strong>Enable two-factor authentication</strong> - 2FA significantly improves account
                security by requiring a second verification step beyond your password<br>
              • <strong>Use a strong password</strong> - Choose a unique password with mix of uppercase,
                lowercase, numbers, and special characters<br>
              • <strong>Monitor active sessions</strong> - Regularly review active sessions and revoke any
                you don't recognize<br>
              • <strong>Keep contact information current</strong> - Outdated email or phone numbers prevent
                important notifications from reaching you<br>
              • <strong>Choose appropriate theme</strong> - Light or dark theme based on your preference and
                working environment<br>
              • <strong>Review last login time</strong> - Check your last login timestamp to detect any
                unauthorized access attempts<br>
              • <strong>Save backup codes safely</strong> - When enabling 2FA, save backup codes in a secure
                location in case you lose access to your authenticator<br>
              • <strong>Manage trusted devices</strong> - If using 2FA, periodically review and remove
                trusted devices you no longer use<br>
              • <strong>Change password periodically</strong> - Regular password changes enhance account
                security, especially if you suspect compromise<br>
              • <strong>Don't share account credentials</strong> - Each user should have their own account
                for proper accountability and audit trails<br>
              • Changes to your profile information take effect immediately throughout the system
            </p>
          </InfoButton>
        </div>
        <Link :href="route('profile.edit')">
          <Button>Edit Profile</Button>
        </Link>
      </div>

      <!-- Profile Header -->
      <Card class="p-6">
        <div class="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
          <Avatar
            :src="user.profile_photo_url"
            :name="user.full_name"
            size="xl"
            class="flex-shrink-0"
          />
          <div class="flex-1 text-center sm:text-left">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {{ user.full_name }}
            </h2>
            <p class="text-gray-600 dark:text-gray-400 mt-1">{{ user.email }}</p>
            <div class="mt-3 flex flex-wrap gap-2 justify-center sm:justify-start">
              <Badge v-if="user.email_verified_at" variant="success">
                <svg class="w-4 h-4 mr-1 inline" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                Email Verified
              </Badge>
              <Badge v-else variant="warning">
                <svg class="w-4 h-4 mr-1 inline" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
                Email Not Verified
              </Badge>
              <Badge v-if="user.is_active" variant="success">Active</Badge>
              <Badge v-else variant="danger">Inactive</Badge>
            </div>
          </div>
        </div>
      </Card>

      <!-- Personal Information -->
      <Card>
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Personal Information
          </h3>
        </template>
        <div class="p-6">
          <dl class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="First Name" :value="user.first_name || 'Not set'" />
            <InfoItem label="Last Name" :value="user.last_name || 'Not set'" />
            <InfoItem label="Email Address" :value="user.email" />
            <InfoItem label="Phone Number" :value="user.phone || 'Not set'" />
            <InfoItem label="Theme Preference">
              <template #value>
                <span class="capitalize flex items-center">
                  <svg v-if="user.theme === 'dark'" class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                  <svg v-else class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd" />
                  </svg>
                  {{ user.theme }}
                </span>
              </template>
            </InfoItem>
            <InfoItem label="Member Since">
              <template #value>
                {{ formatDate(user.created_at) }}
              </template>
            </InfoItem>
            <InfoItem label="Last Login" v-if="user.last_login_at">
              <template #value>
                {{ formatDateTime(user.last_login_at) }}
              </template>
            </InfoItem>
          </dl>
        </div>
      </Card>

      <!-- Security -->
      <Card>
        <template #header>
          <div class="flex items-center gap-3">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
              Security Settings
            </h3>
            <InfoButton title="Account Security Settings" variant="default">
              <p>
                <strong class="text-secondary-900 dark:text-white">About this section</strong><br>
                The Security Settings section provides comprehensive controls to protect your account from
                unauthorized access. Here you can manage your password, enable and configure two-factor
                authentication (2FA), monitor all active sessions across different devices and browsers,
                manage trusted devices for 2FA bypass, and review security activity. Strong security practices
                including regular password changes, enabled 2FA, session monitoring, and trusted device
                management significantly reduce the risk of account compromise, protect sensitive data, ensure
                compliance with security policies, and provide audit trails for security reviews.
              </p>
              <p>
                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                • <strong>Password:</strong> Click "Change Password" to update your account password - requires
                  entering current password for verification<br>
                • <strong>Two-Factor Authentication:</strong> Click "Enable 2FA" to set up additional security
                  requiring a code from authenticator app or email on each login<br>
                • <strong>2FA Methods:</strong> Choose between Authenticator App (Google Authenticator, Authy,
                  etc.) or Email Code for receiving verification codes<br>
                • <strong>Backup Codes:</strong> When 2FA is enabled, click "View Codes" to see single-use
                  backup codes for emergencies when you can't access your authenticator<br>
                • <strong>Disable 2FA:</strong> Click "Disable" button next to 2FA status to turn off two-factor
                  authentication - requires password confirmation<br>
                • <strong>Active Sessions:</strong> Click "Manage Sessions" to view all logged-in sessions
                  across devices and browsers, with ability to revoke any session<br>
                • <strong>Current Session Badge:</strong> Your current session is highlighted with blue border
                  and "Current Session" badge<br>
                • <strong>Revoke Session:</strong> Click "Revoke" to end that session immediately - revoking
                  current session logs you out<br>
                • <strong>Revoke All Sessions:</strong> Click "Revoke All" to end all other sessions except
                  your current one - useful if you suspect compromise<br>
                • <strong>Trusted Devices:</strong> If 2FA is enabled, click "Manage Devices" to view devices
                  where you checked "Remember this device for 14 days" during login<br>
                • <strong>Remove Trusted Device:</strong> Click "Remove" to revoke trust status - next login
                  from that device will require 2FA code<br>
                • <strong>Device Expiration:</strong> Trusted devices automatically expire after 14 days and
                  require 2FA re-verification
              </p>
              <p>
                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                • <strong>Enable 2FA immediately</strong> - Two-factor authentication is the single most
                  effective security improvement you can make to your account<br>
                • <strong>Prefer authenticator apps over email</strong> - Authenticator apps (Google
                  Authenticator, Authy) are more secure than email-based codes<br>
                • <strong>Save backup codes securely</strong> - Store backup codes in password manager or
                  secure physical location - don't keep them on same device as authenticator<br>
                • <strong>Never share backup codes</strong> - Backup codes grant full account access - treat
                  them like passwords<br>
                • <strong>Regenerate backup codes after use</strong> - After using backup codes, regenerate
                  a fresh set to maintain security<br>
                • <strong>Use strong, unique passwords</strong> - Password should be 12+ characters with
                  uppercase, lowercase, numbers, and special characters<br>
                • <strong>Don't reuse passwords</strong> - Use different password for this account than other
                  services to prevent credential stuffing attacks<br>
                • <strong>Change password if suspicious activity</strong> - If you see unfamiliar sessions or
                  suspect compromise, change password immediately<br>
                • <strong>Review sessions regularly</strong> - Monthly review of active sessions helps detect
                  unauthorized access early<br>
                • <strong>Revoke sessions on shared computers</strong> - Always manually revoke sessions after
                  using shared or public computers<br>
                • <strong>Trust devices selectively</strong> - Only mark personal devices as trusted - never
                  trust shared, public, or work computers<br>
                • <strong>Remove old trusted devices</strong> - Periodically clean up trusted devices you no
                  longer use or have lost<br>
                • <strong>Logout when device changes hands</strong> - Revoke current session (logout) before
                  selling, giving away, or disposing of devices<br>
                • <strong>Check IP addresses in sessions</strong> - Unfamiliar IP addresses or locations in
                  active sessions may indicate compromise<br>
                • <strong>Password last changed date</strong> - Note when you last changed password - consider
                  changing every 90 days for high security<br>
                • All security changes (password, 2FA, session revocations) take effect immediately
              </p>
            </InfoButton>
          </div>
        </template>
        <div class="p-6">
          <dl class="space-y-6">
            <InfoItem label="Password">
              <template #value>
                <div class="flex items-center space-x-2">
                  <span class="text-gray-500">••••••••</span>
                  <span v-if="user.password_changed_at" class="text-sm text-gray-500">
                    Last changed {{ formatRelativeDate(user.password_changed_at) }}
                  </span>
                </div>
              </template>
              <template #action>
                <Button size="sm" variant="secondary" @click="openPasswordModal">Change Password</Button>
              </template>
            </InfoItem>

            <InfoItem label="Two-Factor Authentication">
              <template #value>
                <div class="flex items-center space-x-2">
                  <Badge :variant="user.two_fa_enabled ? 'success' : 'secondary'">
                    <svg v-if="user.two_fa_enabled" class="w-4 h-4 mr-1 inline" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                    {{ user.two_fa_enabled ? 'Enabled' : 'Disabled' }}
                  </Badge>
                  <span v-if="user.two_fa_enabled && user.two_fa_enabled_at" class="text-sm text-gray-500">
                    Since {{ formatDate(user.two_fa_enabled_at) }}
                  </span>
                </div>
              </template>
              <template #action>
                <div v-if="user.two_fa_enabled" class="flex gap-2">
                  <Button size="sm" variant="secondary" @click="viewBackupCodes">View Codes</Button>
                  <Button size="sm" variant="danger" @click="confirmDisable2FA">Disable</Button>
                </div>
                <Button v-else size="sm" variant="secondary" @click="show2FAModal = true">Enable 2FA</Button>
              </template>
            </InfoItem>

            <InfoItem label="Active Sessions">
              <template #value>
                <div class="flex items-center space-x-2">
                  <span class="font-medium">{{ activeSessions }} {{ activeSessions === 1 ? 'session' : 'sessions' }}</span>
                  <span class="text-sm text-gray-500">currently active</span>
                </div>
              </template>
              <template #action>
                <Button size="sm" variant="secondary" @click="showSessionsModal = true">Manage Sessions</Button>
              </template>
            </InfoItem>

            <InfoItem v-if="user.two_fa_enabled" label="Trusted Devices">
              <template #value>
                <div class="flex items-center space-x-2">
                  <span class="font-medium">{{ trustedDevices?.length || 0 }} {{ (trustedDevices?.length || 0) === 1 ? 'device' : 'devices' }}</span>
                  <span class="text-sm text-gray-500">trusted for 2FA bypass</span>
                </div>
              </template>
              <template #action>
                <Button size="sm" variant="secondary" @click="showTrustedDevicesModal = true">Manage Devices</Button>
              </template>
            </InfoItem>
          </dl>
        </div>
      </Card>

      <!-- Password Change Modal -->
      <Modal :show="showPasswordModal" @close="closePasswordModal" size="md">
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Change Password
          </h3>
        </template>
        <template #default>
          <div class="space-y-4">
            <Input
              v-model="currentPassword"
              type="password"
              label="Current Password"
              placeholder="Enter your current password"
              required
              showPasswordToggle
              :error="currentPasswordError"
            />
            <Input
              v-model="newPassword"
              type="password"
              label="New Password"
              placeholder="Enter your new password"
              required
              showPasswordToggle
              :error="newPasswordError"
            />
            <Input
              v-model="confirmPassword"
              type="password"
              label="Confirm New Password"
              placeholder="Confirm your new password"
              required
              showPasswordToggle
              :error="passwordMatchError"
            />
            <div class="flex justify-end gap-3 pt-4">
              <Button variant="secondary" @click="closePasswordModal">
                Cancel
              </Button>
              <Button @click="changePassword" :disabled="changingPassword || !currentPassword || !newPassword || !confirmPassword || passwordMatchError">
                {{ changingPassword ? 'Changing...' : 'Change Password' }}
              </Button>
            </div>
          </div>
        </template>
      </Modal>

      <!-- 2FA Setup Modal -->
      <Modal :show="show2FAModal" @close="close2FAModal" size="md">
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Enable Two-Factor Authentication
          </h3>
        </template>
        <template #default>
          <!-- Method Selection -->
          <div v-if="!twoFactorMethod" class="space-y-4">
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Choose how you want to receive your two-factor authentication codes:
            </p>
            <div class="space-y-3">
              <button
                @click="select2FAMethod('authenticator')"
                class="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors text-left group"
              >
                <div class="flex items-start">
                  <div class="flex-shrink-0">
                    <svg class="w-6 h-6 text-gray-400 group-hover:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100">Authenticator App</p>
                    <p class="text-sm text-gray-500 dark:text-gray-400">Use Google Authenticator or any compatible app</p>
                  </div>
                </div>
              </button>
              <button
                @click="select2FAMethod('email')"
                class="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors text-left group"
              >
                <div class="flex items-start">
                  <div class="flex-shrink-0">
                    <svg class="w-6 h-6 text-gray-400 group-hover:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100">Email Code</p>
                    <p class="text-sm text-gray-500 dark:text-gray-400">Receive codes via email to {{ user.email }}</p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-else-if="enabling2FA" class="space-y-4 text-center py-8">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
              <svg class="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <p class="text-gray-600 dark:text-gray-400">
              {{ twoFactorMethod === 'authenticator' ? 'Generating QR code...' : 'Sending verification email...' }}
            </p>
          </div>

          <!-- Authenticator Setup -->
          <div v-else-if="twoFactorMethod === 'authenticator' && twoFactorQrCode" class="space-y-4">
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Scan this QR code with Google Authenticator or any compatible authenticator app.
            </p>
            <div class="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 flex justify-center">
              <img :src="twoFactorQrCode" alt="2FA QR Code" class="w-48 h-48" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                Or enter this code manually:
              </label>
              <code class="block p-3 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono break-all">
                {{ twoFactorSecret }}
              </code>
            </div>
            <Input
              v-model="verificationCode"
              label="Verification Code"
              placeholder="Enter 6-digit code"
              help="Enter the 6-digit code from your authenticator app"
              maxlength="6"
            />
            <div class="flex justify-end gap-3">
              <Button variant="secondary" @click="close2FAModal">
                Cancel
              </Button>
              <Button @click="confirm2FA" :disabled="confirming2FA || verificationCode.length !== 6">
                {{ confirming2FA ? 'Verifying...' : 'Confirm & Enable' }}
              </Button>
            </div>
          </div>

          <!-- Email Setup -->
          <div v-else-if="twoFactorMethod === 'email'" class="space-y-4">
            <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p class="text-sm text-blue-800 dark:text-blue-200">
                A verification code has been sent to <strong>{{ user.email }}</strong>
              </p>
            </div>
            <Input
              v-model="verificationCode"
              label="Verification Code"
              placeholder="Enter 6-digit code"
              help="Enter the 6-digit code from your email"
              maxlength="6"
            />
            <div class="flex justify-end gap-3">
              <Button variant="secondary" @click="close2FAModal">
                Cancel
              </Button>
              <Button @click="confirm2FA" :disabled="confirming2FA || verificationCode.length !== 6">
                {{ confirming2FA ? 'Verifying...' : 'Confirm & Enable' }}
              </Button>
            </div>
          </div>
        </template>
      </Modal>

      <!-- Backup Codes Modal -->
      <Modal :show="showBackupCodesModal" @close="showBackupCodesModal = false" size="md">
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Backup Codes
          </h3>
        </template>
        <template #default>
          <div class="space-y-4">
            <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div class="flex">
                <svg class="h-5 w-5 text-yellow-600 dark:text-yellow-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
                <div>
                  <h4 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Save These Codes
                  </h4>
                  <p class="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                    Store these backup codes in a safe place. Each code can only be used once.
                  </p>
                </div>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg font-mono text-sm">
              <div v-for="(code, index) in backupCodes" :key="index" class="p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 text-center">
                {{ code }}
              </div>
            </div>
            <div class="flex justify-between gap-3">
              <Button variant="secondary" @click="regenerateBackupCodes">
                Regenerate
              </Button>
              <div class="flex gap-3">
                <Button variant="secondary" @click="downloadBackupCodes">
                  Download
                </Button>
                <Button @click="showBackupCodesModal = false">
                  Close
                </Button>
              </div>
            </div>
          </div>
        </template>
      </Modal>

      <!-- Disable 2FA Modal -->
      <Modal :show="showDisable2FAModal" @close="closeDisable2FAModal" size="md">
        <template #header>
          Disable Two-Factor Authentication
        </template>

        <div class="space-y-4">
          <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div class="flex">
              <svg class="h-5 w-5 text-yellow-600 dark:text-yellow-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
              <div>
                <h4 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Security Warning
                </h4>
                <p class="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                  Disabling two-factor authentication will make your account less secure. You will only need your password to login.
                </p>
              </div>
            </div>
          </div>
          <Input
            v-model="disablePassword"
            type="password"
            label="Confirm Your Password"
            placeholder="Enter your account password"
            help="Enter your account password to confirm this action"
            required
            autocomplete="current-password"
          />
          <div class="flex justify-end gap-3">
            <Button variant="secondary" @click="closeDisable2FAModal" :disabled="disabling2FA">
              Cancel
            </Button>
            <Button variant="danger" @click="disable2FA" :disabled="disabling2FA || !disablePassword">
              {{ disabling2FA ? 'Disabling...' : 'Disable 2FA' }}
            </Button>
          </div>
        </div>
      </Modal>

      <!-- Sessions Modal -->
      <Modal :show="showSessionsModal" @close="showSessionsModal = false" size="2xl">
        <template #header>
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
              Active Sessions
            </h3>
            <Button
              v-if="sessions.length > 1"
              size="sm"
              variant="danger"
              @click="confirmAction = { show: true, title: 'Revoke All Sessions', message: 'Are you sure you want to revoke all other sessions?', confirmText: 'Revoke All', variant: 'danger', action: revokeAllSessions, processing: false }"
            >
              Revoke All
            </Button>
          </div>
        </template>
        <template #default>
          <div class="space-y-3">
            <div
              v-for="session in sessions"
              :key="session.id"
              class="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
              :class="{ 'ring-2 ring-blue-500': session.is_current }"
            >
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <h4 class="font-medium text-gray-900 dark:text-gray-100">
                      {{ session.user_agent || 'Unknown Browser' }}
                    </h4>
                    <Badge v-if="session.is_current" variant="success" size="sm">
                      Current Session
                    </Badge>
                  </div>
                  <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    IP: {{ session.ip_address }}
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    Last active: {{ formatRelativeDate(session.last_activity) }}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  @click="confirmAction = { show: true, title: session.is_current ? 'Logout from Current Session' : 'Revoke Session', message: session.is_current ? 'Are you sure you want to logout? You will be redirected to the login page.' : 'Are you sure you want to revoke this session?', confirmText: session.is_current ? 'Logout' : 'Revoke', variant: 'danger', action: () => revokeSession(session.id, session.is_current), processing: false }"
                >
                  {{ session.is_current ? 'Logout' : 'Revoke' }}
                </Button>
              </div>
            </div>
            <div v-if="sessions.length === 0" class="text-center py-8 text-gray-500">
              <p>No active sessions</p>
            </div>
          </div>
        </template>
      </Modal>

      <!-- Trusted Devices Modal -->
      <Modal :show="showTrustedDevicesModal" @close="showTrustedDevicesModal = false" size="2xl">
        <template #header>
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
              Trusted Devices
            </h3>
            <Button
              v-if="trustedDevices && trustedDevices.length > 0"
              size="sm"
              variant="danger"
              @click="() => { confirmAction.show = true; confirmAction.title = 'Remove All Trusted Devices'; confirmAction.message = 'Are you sure you want to remove all trusted devices? You will need to enter a 2FA code on your next login.'; confirmAction.confirmText = 'Remove All Devices'; confirmAction.variant = 'danger'; confirmAction.action = revokeAllDevices; confirmAction.processing = false; }"
            >
              Remove All
            </Button>
          </div>
        </template>
        <template #default>
          <div class="space-y-3">
            <div
              v-for="device in trustedDevices || []"
              :key="device.id"
              class="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
              :class="{ 'ring-2 ring-blue-500': device.is_current }"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-2">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <h4 class="font-medium text-gray-900 dark:text-gray-100">
                      {{ device.device_name || 'Unknown Device' }}
                    </h4>
                    <Badge v-if="device.is_current" variant="success" size="sm">
                      Current Device
                    </Badge>
                    <Badge v-else-if="!device.is_valid" variant="secondary" size="sm">
                      Expired
                    </Badge>
                  </div>
                  <div class="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    <p>IP Address: <span class="font-mono">{{ device.ip_address }}</span></p>
                    <p>Added: {{ device.created_at }}</p>
                    <p>Last Used: {{ device.last_used_at }}</p>
                    <p>Expires: {{ device.expires_at }}</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  @click="() => { confirmAction.show = true; confirmAction.title = 'Remove Trusted Device'; confirmAction.message = 'Are you sure you want to remove ' + (device.device_name || 'this device') + '? You will need to enter a 2FA code on your next login from this device.'; confirmAction.confirmText = 'Remove Device'; confirmAction.variant = 'danger'; confirmAction.action = () => revokeDevice(device.id); confirmAction.processing = false; }"
                >
                  Remove
                </Button>
              </div>
            </div>
            <div v-if="!trustedDevices || trustedDevices.length === 0" class="text-center py-8 text-gray-500">
              <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p class="mt-4">No trusted devices</p>
              <p class="text-sm mt-1">Check "Remember this device for 14 days" during 2FA login to add a device</p>
            </div>
          </div>
        </template>
      </Modal>

      <!-- Confirmation Modal -->
      <ConfirmModal
        :show="confirmAction.show"
        :title="confirmAction.title"
        :message="confirmAction.message"
        :confirm-text="confirmAction.confirmText"
        :variant="confirmAction.variant"
        :confirm-variant="confirmAction.variant"
        :processing="confirmAction.processing"
        @confirm="executeConfirmAction"
        @cancel="cancelConfirmAction"
      />
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Avatar from '@/Components/Common/Avatar.vue';
import Badge from '@/Components/Common/Badge.vue';
import InfoItem from '@/Components/Common/InfoItem.vue';
import Modal from '@/Components/Common/Modal.vue';
import Input from '@/Components/Common/Input.vue';
import ConfirmModal from '@/Components/Common/ConfirmModal.vue';
import InfoButton from '@/Components/Common/InfoButton.vue';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import axios from 'axios';

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
  companies: {
    type: Array,
    default: () => [],
  },
  activeSessions: {
    type: Number,
    default: 1,
  },
  sessions: {
    type: Array,
    default: () => [],
  },
});

const { success, error } = useToast();

// Password change modal
const showPasswordModal = ref(false);
const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const changingPassword = ref(false);
const currentPasswordError = ref(null);
const newPasswordError = ref(null);

// Real-time password matching validation
const passwordMatchError = computed(() => {
  if (!confirmPassword.value) return null;
  if (newPassword.value !== confirmPassword.value) {
    return 'Passwords do not match';
  }
  return null;
});

// 2FA modals
const show2FAModal = ref(false);
const showBackupCodesModal = ref(false);
const showDisable2FAModal = ref(false);
const enabling2FA = ref(false);
const confirming2FA = ref(false);
const disabling2FA = ref(false);
const twoFactorMethod = ref(null);
const twoFactorQrCode = ref(null);
const twoFactorSecret = ref(null);
const verificationCode = ref('');
const disablePassword = ref('');
const backupCodes = ref([]);

// Sessions
const showSessionsModal = ref(false);

// Trusted Devices
const showTrustedDevicesModal = ref(false);
const trustedDevices = ref([]);

// Confirm action
const confirmAction = ref({
  show: false,
  title: '',
  message: '',
  confirmText: '',
  variant: 'danger',
  action: null,
  processing: false,
});

/**
 * Fetch trusted devices on mount
 */
onMounted(() => {
  if (props.user.two_fa_enabled) {
    fetchTrustedDevices();
  }
});

/**
 * Open password change modal
 */
const openPasswordModal = () => {
  // Clear all fields and errors
  currentPassword.value = '';
  newPassword.value = '';
  confirmPassword.value = '';
  currentPasswordError.value = null;
  newPasswordError.value = null;
  showPasswordModal.value = true;
};

/**
 * Close password change modal
 */
const closePasswordModal = () => {
  showPasswordModal.value = false;
  currentPassword.value = '';
  newPassword.value = '';
  confirmPassword.value = '';
  currentPasswordError.value = null;
  newPasswordError.value = null;
};

/**
 * Change Password
 */
const changePassword = () => {
  // Clear previous errors
  currentPasswordError.value = null;
  newPasswordError.value = null;

  if (newPassword.value !== confirmPassword.value) {
    return; // passwordMatchError computed will handle this
  }

  changingPassword.value = true;
  router.put(
    route('profile.password.update'),
    {
      current_password: currentPassword.value,
      password: newPassword.value,
      password_confirmation: confirmPassword.value,
    },
    {
      preserveScroll: true,
      onSuccess: () => {
        closePasswordModal();
        changingPassword.value = false;
      },
      onError: (errors) => {
        changingPassword.value = false;
        if (errors.current_password) {
          currentPasswordError.value = errors.current_password[0];
        } else if (errors.password) {
          newPasswordError.value = errors.password[0];
        }
      },
    }
  );
};

/**
 * Select 2FA method and enable
 */
const select2FAMethod = (method) => {
  twoFactorMethod.value = method;
  enabling2FA.value = true;

  router.post(
    route('profile.2fa.enable'),
    { method },
    {
      preserveScroll: true,
      onSuccess: (page) => {
        enabling2FA.value = false;

        // Access data from twoFactorData (shared via HandleInertiaRequests middleware)
        if (page.props.twoFactorData) {
          const data = page.props.twoFactorData;

          if (data.method === 'authenticator') {
            // Generate QR code URL using QuickChart.io (free, open-source alternative to Google Charts)
            // data.qr_code contains the otpauth:// URL
            const qrUrl = encodeURIComponent(data.qr_code);
            twoFactorQrCode.value = `https://quickchart.io/qr?text=${qrUrl}&size=200`;

            twoFactorSecret.value = data.secret;
            backupCodes.value = data.backup_codes;
          } else if (data.method === 'email') {
            backupCodes.value = data.backup_codes;
          }
        }
      },
      onError: (errors) => {
        enabling2FA.value = false;
        twoFactorMethod.value = null;
        // Show specific error message from backend
        if (errors['2fa']) {
          error(errors['2fa']);
        } else {
          error('Failed to enable 2FA. Please try again.');
        }
      },
    }
  );
};

/**
 * Confirm 2FA
 */
const confirm2FA = () => {
  confirming2FA.value = true;
  router.post(
    route('profile.2fa.confirm'),
    { code: verificationCode.value },
    {
      preserveScroll: true,
      onSuccess: () => {
        confirming2FA.value = false;
        close2FAModal();
        showBackupCodesModal.value = true;
        success('Two-factor authentication enabled successfully');
      },
      onError: (errors) => {
        confirming2FA.value = false;
        // Show specific error message from backend
        if (errors.code) {
          error(errors.code);
        } else {
          error('Invalid verification code');
        }
      },
    }
  );
};

/**
 * Close 2FA modal
 */
const close2FAModal = () => {
  show2FAModal.value = false;
  twoFactorMethod.value = null;
  twoFactorQrCode.value = null;
  twoFactorSecret.value = null;
  verificationCode.value = '';
  enabling2FA.value = false;
  confirming2FA.value = false;
};

/**
 * View backup codes
 */
const viewBackupCodes = async () => {
  try {
    const response = await axios.get(route('profile.2fa.backup-codes'));
    backupCodes.value = response.data.backup_codes;
    showBackupCodesModal.value = true;
  } catch (err) {
    console.error('Failed to fetch backup codes:', err);
    error('Failed to load backup codes');
  }
};

/**
 * Regenerate backup codes
 */
const regenerateBackupCodes = async () => {
  if (confirm('Are you sure? Your old codes will no longer work.')) {
    try {
      const response = await axios.post(route('profile.2fa.backup-codes.regenerate'));
      backupCodes.value = response.data.backup_codes;
      success('Backup codes regenerated successfully');
    } catch (err) {
      console.error('Failed to regenerate backup codes:', err);
      error(err.response?.data?.error || 'Failed to regenerate backup codes');
    }
  }
};

/**
 * Download backup codes
 */
const downloadBackupCodes = () => {
  const content = backupCodes.value.join('\n');
  const blob = new Blob([content], { type: 'text/plain' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'backup-codes.txt';
  a.click();
  window.URL.revokeObjectURL(url);
};

/**
 * Confirm disable 2FA
 */
const confirmDisable2FA = () => {
  disablePassword.value = '';
  showDisable2FAModal.value = true;
};

/**
 * Disable 2FA
 */
const disable2FA = () => {
  disabling2FA.value = true;
  router.post(route('profile.2fa.disable'), {
    password: disablePassword.value,
  }, {
    preserveScroll: true,
    onSuccess: () => {
      disabling2FA.value = false;
      showDisable2FAModal.value = false;
      disablePassword.value = '';
      success('Two-factor authentication disabled');
    },
    onError: (errors) => {
      disabling2FA.value = false;
      if (errors.password) {
        error(errors.password[0]);
      } else {
        error('Failed to disable 2FA');
      }
    },
  });
};

/**
 * Close disable 2FA modal
 */
const closeDisable2FAModal = () => {
  showDisable2FAModal.value = false;
  disablePassword.value = '';
};

/**
 * Revoke session
 */
const revokeSession = (sessionId, isCurrent = false) => {
  confirmAction.value.processing = true;
  router.delete(route('profile.sessions.revoke', sessionId), {
    preserveScroll: false, // Don't preserve scroll to ensure proper page updates
    onSuccess: () => {
      // Backend handles redirect for current session
      // For other sessions, just show success message
      if (!isCurrent) {
        success('Session revoked successfully');
        cancelConfirmAction();
      }
      // If isCurrent, backend redirects to login automatically
    },
    onError: () => {
      error('Failed to revoke session');
      confirmAction.value.processing = false;
    },
  });
};

/**
 * Revoke all sessions
 */
const revokeAllSessions = () => {
  confirmAction.value.processing = true;
  router.delete(route('profile.sessions.revoke-all'), {
    preserveScroll: true,
    onSuccess: () => {
      success('All other sessions revoked successfully');
      cancelConfirmAction();
    },
    onError: () => {
      error('Failed to revoke sessions');
      confirmAction.value.processing = false;
    },
  });
};

/**
 * Execute confirm action
 */
const executeConfirmAction = () => {
  if (confirmAction.value.action) {
    confirmAction.value.action();
  }
};

/**
 * Cancel confirm action
 */
const cancelConfirmAction = () => {
  confirmAction.value = {
    show: false,
    title: '',
    message: '',
    confirmText: '',
    variant: 'danger',
    action: null,
    processing: false,
  };
};

/**
 * Format language code to display name
 */
const formatLanguage = (code) => {
  const languages = {
    'en': 'English',
    'ar': 'العربية (Arabic)',
  };
  return languages[code] || code;
};

/**
 * Format role slug to display name
 */
const formatRole = (role) => {
  const roles = {
    'super_admin': 'Super Admin',
    'company_admin': 'Admin',
    'company_subscriber': 'Subscriber',
    'company_user': 'User',
    'support_agent': 'Support Agent',
    'department_head': 'Department Head',
  };
  return roles[role] || role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Format date (e.g., "Jan 15, 2024")
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date and time (e.g., "Jan 15, 2024 at 3:45 PM")
 */
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Format relative date (e.g., "2 days ago")
 */
const formatRelativeDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
};

/**
 * Get badge variant based on status
 */
const getStatusVariant = (status) => {
  const variants = {
    'active': 'success',
    'invited': 'warning',
    'suspended': 'danger',
    'inactive': 'secondary',
  };
  return variants[status] || 'secondary';
};

/**
 * Switch to a different company
 */
const switchCompany = (companyId) => {
  router.post(
    route('company.switch', companyId),
    {},
    {
      preserveState: false,
      preserveScroll: false,
      // Controller handles redirect to dashboard
    }
  );
};

/**
 * Fetch trusted devices
 */
const fetchTrustedDevices = async () => {
  try {
    const response = await axios.get(route('profile.trusted-devices.index'));
    trustedDevices.value = response.data.trusted_devices || [];
  } catch (err) {
    console.error('Failed to fetch trusted devices:', err);
    trustedDevices.value = [];
    error('Failed to load trusted devices');
  }
};

/**
 * Confirm revoke device
 */
const confirmRevokeDevice = (device) => {
  confirmAction.value = {
    show: true,
    title: 'Remove Trusted Device',
    message: `Are you sure you want to remove ${device.device_name || 'this device'}? You will need to enter a 2FA code on your next login from this device.`,
    confirmText: 'Remove Device',
    variant: 'danger',
    action: () => revokeDevice(device.id),
    processing: false,
  };
};

/**
 * Revoke device
 */
const revokeDevice = async (deviceId) => {
  confirmAction.value.processing = true;
  try {
    await axios.delete(route('profile.trusted-devices.revoke', deviceId));
    success('Trusted device removed successfully');
    await fetchTrustedDevices();
    cancelConfirmAction();
  } catch (err) {
    error('Failed to remove trusted device');
    confirmAction.value.processing = false;
  }
};

/**
 * Confirm revoke all devices
 */
const confirmRevokeAllDevices = () => {
  confirmAction.value = {
    show: true,
    title: 'Remove All Trusted Devices',
    message: 'Are you sure you want to remove all trusted devices? You will need to enter a 2FA code on your next login.',
    confirmText: 'Remove All Devices',
    variant: 'danger',
    action: revokeAllDevices,
    processing: false,
  };
};

/**
 * Revoke all devices
 */
const revokeAllDevices = async () => {
  confirmAction.value.processing = true;
  try {
    await axios.delete(route('profile.trusted-devices.revoke-all'));
    success('All trusted devices removed successfully');
    await fetchTrustedDevices();
    cancelConfirmAction();
  } catch (err) {
    error('Failed to remove trusted devices');
    confirmAction.value.processing = false;
  }
};
</script>
